import { useEffect, useState } from "react";

import { usePostHog } from "posthog-js/react";
import { useSearchParams } from "react-router-dom";

import {
  ClientAppEvent,
  SHARED_TEMPLATE_QUERY_PARAM_KEY,
} from "constants/index";
import { useAPI, useRoleContext, useSwrFetch } from "hooks";
import {
  filterAndSortTemplates,
  shipmentTemplatesListUrl,
} from "utils/templates";

import { ShareShipmentTemplateModalContainer } from "./components/ShareShipmentTemplateModal";
import { ShipmentTemplateMenu } from "./ShipmentTemplateMenu";

import type { ShipmentTemplate } from "types/shipment";

// How long a template shared via a notification stays highlighted before fading.
export const SHARED_TEMPLATE_HIGHLIGHT_MS = 800;

interface Props {
  onTemplateClick: (template: ShipmentTemplate) => void;
  /**
   * Held in the loading state until the caller can resolve a template's
   * carriers against the poster's network. Applying one before then would
   * seed the form with recipients that are never recalculated.
   */
  waitingOnCallerData?: boolean;
}

export function ShipmentTemplateMenuContainer(props: Props) {
  const { onTemplateClick, waitingOnCallerData = false } = props;
  const { role } = useRoleContext();
  const { api } = useAPI();
  const posthog = usePostHog();
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null,
  );
  const [deletedTemplate, setDeletedTemplate] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [templateBeingShared, setTemplateBeingShared] =
    useState<ShipmentTemplate | null>(null);
  const [shareConfirmation, setShareConfirmation] = useState<string | null>(
    null,
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const sharedTemplateParam = searchParams.get(SHARED_TEMPLATE_QUERY_PARAM_KEY);

  const templatesUrl =
    role?.company && role?.id ? shipmentTemplatesListUrl(role.id) : null;

  const {
    data: templates,
    loading,
    error: fetchError,
    refetch,
  } = useSwrFetch<ShipmentTemplate[]>({
    url: templatesUrl,
    errorMessage: "Failed to fetch templates. Please contact us.",
  });

  const templatesReady = templates != null && !loading && !waitingOnCallerData;

  // Arriving from a "template shared with you" notification: highlight that
  // template if the user still has it.
  const highlightedTemplateId = templatesReady
    ? (templates.find((template) => String(template.id) === sharedTemplateParam)
        ?.id ?? null)
    : null;

  // Removing the param ends the highlight and stops a refresh replaying it.
  useEffect(() => {
    if (sharedTemplateParam == null || !templatesReady) return undefined;
    const timer = setTimeout(
      () =>
        setSearchParams(
          (previous) => {
            previous.delete(SHARED_TEMPLATE_QUERY_PARAM_KEY);
            return previous;
          },
          { replace: true },
        ),
      SHARED_TEMPLATE_HIGHLIGHT_MS,
    );
    return () => clearTimeout(timer);
  }, [sharedTemplateParam, templatesReady, setSearchParams]);

  const onTemplateClicked = (template: ShipmentTemplate) => {
    onTemplateClick(template);
    setSelectedTemplateId(template.id);
  };

  const onDeleteTemplate = (template: ShipmentTemplate) => {
    setDeleteError(null);
    api<unknown>("delete", `/shipment-templates/${template.id}`)
      .then(() => {
        setDeletedTemplate(true);
        refetch();
      })
      .catch((e) => {
        setDeleteError(
          e?.response?.data?.message ?? "Failed to delete shipment template",
        );
      });
  };

  const onShareTemplate = (template: ShipmentTemplate) => {
    posthog?.capture(ClientAppEvent.SHIPMENT_TEMPLATE_SHARE_MODAL_OPENED, {
      role,
      template_id: template.id,
    });
    setTemplateBeingShared(template);
  };

  const filteredAndSortedTemplates = filterAndSortTemplates(
    templates ?? [],
    searchQuery,
  );

  return (
    <>
      <ShipmentTemplateMenu
        error={fetchError ?? deleteError}
        loading={loading || waitingOnCallerData}
        templates={filteredAndSortedTemplates}
        onTemplateClicked={onTemplateClicked}
        selectedTemplateId={selectedTemplateId}
        highlightedTemplateId={highlightedTemplateId}
        onDeleteTemplate={onDeleteTemplate}
        onShareTemplate={onShareTemplate}
        shareConfirmation={shareConfirmation}
        onCloseShareConfirmation={() => setShareConfirmation(null)}
        deletedTemplate={deletedTemplate}
        onCloseDeletedTemplate={() => {
          setDeletedTemplate(false);
        }}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
      />
      {templateBeingShared && (
        <ShareShipmentTemplateModalContainer
          template={templateBeingShared}
          onClose={() => setTemplateBeingShared(null)}
          onShared={setShareConfirmation}
        />
      )}
    </>
  );
}
